import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Account, PortalUserData } from '../../../types';
import { getUser } from '../../../store/User';
import { assignPermissionSet, unassignPermissionSet, usePermissionSetAssignments } from '../../../store/PermissionSets';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { fetchPortalUsers } from '../../../Api/Admin/PortalUsers/fetchPortalUsers';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { Icon } from '../../Atoms/Icon';
import { AccountSelectionForm } from '../UserCreation';
import { extractApiErrorMessage } from '../McpAccess/extractApiErrorMessage';
import { assignmentForUser, userDisplayName, usersOnSet } from './userAssignmentHelpers';

export interface SetUserAssignmentsProps {
  permissionSetId: string;
}

interface MembersTableProps {
  members: PortalUserData[];
  submitting: boolean;
  onRemove: (user: PortalUserData) => void;
}

const MembersTable = ({ members, submitting, onRemove }: MembersTableProps) => {
  if (!members.length)
    return <Typography color="text.secondary">No users are assigned to this set for this account yet.</Typography>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>User</TableCell>
          <TableCell>Email</TableCell>
          <TableCell align="right">Remove</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {members.map((user) => (
          <TableRow key={user.id}>
            <TableCell>{userDisplayName(user)}</TableCell>
            <TableCell>{user.email}</TableCell>
            <TableCell align="right">
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  aria-label={`Remove ${userDisplayName(user)}`}
                  disabled={submitting}
                  onClick={() => onRemove(user)}>
                  <Icon type="trash" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const SetUserAssignments = ({ permissionSetId }: SetUserAssignmentsProps) => {
  const { setSnack } = useSnackbar();
  const { assignments, submitting } = usePermissionSetAssignments();
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [roster, setRoster] = useState<PortalUserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [toAdd, setToAdd] = useState<PortalUserData[]>([]);
  const accountId = selectedAccount?.id ?? '';

  useEffect(() => {
    if (!accountId) {
      setRoster([]);
      return;
    }
    let active = true;
    setLoading(true);
    setToAdd([]);
    fetchPortalUsers(accountId).then(({ data, error }) => {
      if (!active) return;
      if (error)
        setSnack({ message: extractApiErrorMessage(error, 'Could not load users'), type: 'error', open: true });
      setRoster(data ?? []);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [accountId, setSnack]);

  const members = useMemo(
    () => usersOnSet(roster, assignments, accountId, permissionSetId),
    [roster, assignments, accountId, permissionSetId]
  );
  const memberIds = useMemo(() => new Set(members.map((user) => user.id)), [members]);
  const candidates = useMemo(() => roster.filter((user) => !memberIds.has(user.id)), [roster, memberIds]);

  const handleAdd = useCallback(async () => {
    const outcomes = await Promise.all(
      toAdd.map(async (user) => ({
        user,
        error: (
          await assignPermissionSet({
            permission_set_id: permissionSetId,
            portal_user_id: Number(user.id),
            account_id: accountId
          })
        ).error
      }))
    );
    const failed = outcomes.filter((outcome) => outcome.error);
    if (failed.length)
      setSnack({
        message: `Could not assign: ${failed.map((outcome) => userDisplayName(outcome.user)).join(', ')}`,
        type: 'error',
        open: true
      });
    setToAdd(failed.map((outcome) => outcome.user));
  }, [toAdd, permissionSetId, accountId, setSnack]);

  const handleRemove = useCallback(
    async (user: PortalUserData) => {
      const assignment = assignmentForUser(assignments, accountId, user.id);
      if (!assignment) return;
      const result = await unassignPermissionSet(assignment.id);
      if (result.error)
        setSnack({ message: extractApiErrorMessage(result.error, 'Could not unassign'), type: 'error', open: true });
    },
    [assignments, accountId, setSnack]
  );

  return (
    <Stack
      spacing={2}
      data-cy="set-user-assignments">
      <Typography variant="subtitle2">User Assignments</Typography>
      <AccountSelectionForm
        accounts={getUser()?.accounts}
        selectedAccount={selectedAccount}
        setSelectedAccount={(account) => setSelectedAccount(account ?? null)}
        showLabel={false}
        styles="w-96"
      />
      {!selectedAccount ? (
        <Typography color="text.secondary">Select an account to manage its users.</Typography>
      ) : loading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center">
            <Autocomplete
              multiple
              className="w-full"
              options={candidates}
              value={toAdd}
              onChange={(_event, value) => setToAdd(value)}
              getOptionLabel={(user) => userDisplayName(user)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add users"
                />
              )}
            />
            <button
              type="button"
              className={DEFAULT_ADVANCE_BUTTON_STYLE}
              disabled={!toAdd.length || submitting}
              onClick={handleAdd}>
              Add
            </button>
          </Stack>
          <MembersTable
            members={members}
            submitting={submitting}
            onRemove={handleRemove}
          />
        </>
      )}
    </Stack>
  );
};
